import React, { useMemo, useState } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// Local to the CP Site Visit modal — deliberately NOT the shared
// Subcomponents/DropDown. That one hardcodes `width: 500px` (px, so it does not
// follow the app's 10px→7px root-font scaling) and a 150px menu, which overflows
// this modal's ~277px content box on a phone. It is imported by 68 other files,
// so it is left untouched; this component sizes in %/rem instead.

const norm = (v) =>
  String(v ?? "")
    .toLowerCase()
    .trim();

export default function SearchSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "Select",
  helperText = "",
  searchable = false,
  searchPlaceholder = "Search",
  emptyText = "No matches found",
  disabled = false,
}) {
  const [search, setSearch] = useState("");
  const hasError = helperText !== "";

  const visible = useMemo(() => {
    const q = norm(search);
    if (!searchable || !q) return options;
    return options.filter((o) => norm(o.label).includes(q));
  }, [options, search, searchable]);

  const selectedLabel =
    options.find((o) => o.value === value)?.label ?? (value || "");

  return (
    <FormControl fullWidth error={hasError} disabled={disabled}>
      <Select
        displayEmpty
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        // Drop the query when the menu closes so reopening starts clean.
        onClose={() => setSearch("")}
        IconComponent={KeyboardArrowDownIcon}
        renderValue={() =>
          selectedLabel ? (
            <Box
              component="span"
              sx={{
                display: "block",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                color: "#101828",
              }}
            >
              {selectedLabel}
            </Box>
          ) : (
            <Box component="span" sx={{ color: "#98A2B3" }}>
              {placeholder}
            </Box>
          )
        }
        MenuProps={{
          // Keep focus on the search field instead of the option list.
          autoFocus: false,
          PaperProps: {
            sx: {
              // Tall enough to browse, never taller than the viewport.
              maxHeight: "min(48vh, 34rem)",
              borderRadius: "0.8rem",
              boxShadow: "0px 4px 16px rgba(16, 24, 40, 0.12)",
            },
          },
          MenuListProps: { sx: { paddingTop: 0, paddingBottom: 0 } },
        }}
        sx={{
          width: "100%",
          fontFamily: "Inter-Regular",
          fontSize: "1.6rem",
          lineHeight: "2.4rem",
          textAlign: "left",
          background: "#F2F4F7",
          border: "1px solid #EFF8FF",
          borderRadius: "0.8rem !important",
          boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
          "& .MuiSelect-select": {
            padding: "0.8rem 1.4rem",
            minHeight: "unset !important",
          },
          "& fieldset": { border: "none" },
          "& .MuiSelect-icon": {
            width: "2.4rem",
            height: "2.4rem",
            right: "0.8rem",
            top: "unset",
          },
          "&.Mui-focused": {
            border: "1px solid #84CAFF",
            background: "#FFFFFF",
            boxShadow:
              "0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #D1E9FF",
          },
          "&.Mui-error": { border: "1px solid #d32f2f" },
        }}
      >
        {searchable && (
          <ListSubheader
            sx={{
              padding: "0.8rem",
              background: "#FFFFFF",
              position: "sticky",
              top: 0,
              zIndex: 2,
              lineHeight: "unset",
              borderBottom: "1px solid #F2F4F7",
            }}
          >
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={search}
              placeholder={searchPlaceholder}
              onChange={(e) => setSearch(e.target.value)}
              // The Select swallows keystrokes for its own typeahead, which would
              // steal what is typed here. Escape is let through so it still closes.
              onKeyDown={(e) => {
                if (e.key !== "Escape") e.stopPropagation();
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: "2rem", color: "#98A2B3" }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{ style: { fontSize: "1.5rem" } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0.8rem",
                  background: "#F2F4F7",
                  "& fieldset": { borderColor: "#EFF8FF" },
                },
              }}
            />
          </ListSubheader>
        )}

        {visible.length === 0 ? (
          <MenuItem
            disabled
            value=""
            sx={{
              fontFamily: "Inter-Regular",
              fontSize: "1.4rem",
              color: "#98A2B3",
              padding: "1.2rem 1.4rem",
            }}
          >
            {emptyText}
          </MenuItem>
        ) : (
          visible.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                fontFamily: "Inter-Medium",
                fontSize: "1.4rem",
                lineHeight: "2rem",
                color: "#101828",
                padding: "1.2rem 1.4rem",
                borderBottom: "1px solid #F2F4F7",
                // Long project names wrap instead of being clipped.
                whiteSpace: "normal",
                wordBreak: "break-word",
                "&:last-of-type": { borderBottom: "none" },
                "&.Mui-selected": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                },
              }}
            >
              {option.label}
            </MenuItem>
          ))
        )}
      </Select>

      {hasError && (
        <FormHelperText
          sx={{
            fontFamily: "Inter-Medium",
            fontSize: "1.4rem",
            margin: "0.4rem 0 0 0",
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
